import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useRefresh, triggerRefresh } from "@/hooks/useRefresh";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const ROLE_BADGE = {
  owner: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  manager: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  staff: "bg-slate-100 text-slate-700 hover:bg-slate-100",
};

export default function Staff() {
  const { branches } = useBranch();
  const [data, setData] = useState({ members: [], invites: [] });
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", role: "staff", branch_ids: [] });

  useRefresh(() => api.get("/staff").then((r) => setData(r.data)), []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/invite", form);
      toast.success(`Invite sent to ${form.email}`);
      setInviting(false);
      setForm({ email: "", role: "staff", branch_ids: [] });
      triggerRefresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  const update = async (m, patch) => {
    try {
      await api.put(`/staff/${m.membership_id}`, patch);
      toast.success("Member updated");
      triggerRefresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  const revoke = async (inv) => {
    await api.delete(`/staff/invite/${inv.invite_id}`);
    toast.success("Invite revoked");
    triggerRefresh();
  };

  const toggleBranch = (m, branch_id) => {
    const ids = m.branch_ids.includes(branch_id) ? m.branch_ids.filter((b) => b !== branch_id) : [...m.branch_ids, branch_id];
    update(m, { branch_ids: ids });
  };

  return (
    <div className="space-y-4" data-testid="staff-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Staff</h1>
        <Button onClick={() => setInviting(true)} data-testid="invite-staff-btn"><Plus className="h-4 w-4 mr-1" /> Invite by email</Button>
      </div>

      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">Member</th><th className="py-2 px-3">Role</th>
              <th className="py-2 px-3">Branches</th><th className="py-2 px-3">Active</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.membership_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`staff-row-${m.membership_id}`}>
                <td className="py-2 px-3">
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-muted-foreground">{m.email}</div>
                </td>
                <td className="py-2 px-3">
                  <Select value={m.role} onValueChange={(v) => update(m, { role: v })}>
                    <SelectTrigger className="w-28 h-8" data-testid={`staff-role-select-${m.membership_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">owner</SelectItem>
                      <SelectItem value="manager">manager</SelectItem>
                      <SelectItem value="staff">staff</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-3">
                  {m.role === "owner" ? (
                    <span className="text-xs text-muted-foreground">All branches</span>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {branches.map((b) => (
                        <label key={b.branch_id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <Checkbox checked={m.branch_ids.includes(b.branch_id)} onCheckedChange={() => toggleBranch(m, b.branch_id)}
                            data-testid={`staff-branch-${m.membership_id}-${b.branch_id}`} />
                          {b.name.split(" - ")[0]}
                        </label>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2 px-3">
                  <Switch checked={m.active} onCheckedChange={(v) => update(m, { active: v })} data-testid={`staff-active-switch-${m.membership_id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.invites.length > 0 && (
        <div className="border rounded-xl bg-card p-4">
          <div className="font-semibold text-sm mb-2">Pending invites</div>
          <ul className="divide-y">
            {data.invites.map((inv) => (
              <li key={inv.invite_id} className="py-2 flex items-center justify-between gap-3">
                <div className="text-sm">{inv.email} <Badge className={`ml-2 ${ROLE_BADGE[inv.role]}`}>{inv.role}</Badge></div>
                <Button size="sm" variant="ghost" onClick={() => revoke(inv)} data-testid={`revoke-invite-${inv.invite_id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={inviting} onOpenChange={setInviting}>
        <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite staff</DialogTitle></DialogHeader>
          <form onSubmit={invite} className="space-y-4">
            <div className="space-y-2"><Label>Email</Label>
              <Input type="email" data-testid="invite-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">manager</SelectItem>
                  <SelectItem value="staff">staff</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Assigned branches</Label>
              <div className="space-y-2 border rounded-md p-3">
                {branches.map((b) => (
                  <label key={b.branch_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.branch_ids.includes(b.branch_id)}
                      onCheckedChange={(c) => setForm({ ...form, branch_ids: c ? [...form.branch_ids, b.branch_id] : form.branch_ids.filter((x) => x !== b.branch_id) })}
                      data-testid={`invite-branch-${b.branch_id}`} />
                    {b.name}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">When they sign up (email or Google) with this address, they'll join your company automatically.</p>
            </div>
            <Button type="submit" className="w-full" data-testid="invite-submit-btn">Send invite</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
